import { Component, Inject, OnInit, Optional } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ButtonStyle } from '@constants/button/1.0';
import { CatalogItem } from '@interfaces/catalog/1.0/catalog-item';
import { Composite } from '@interfaces/composite/1.0';
import { Product } from '@interfaces/product/1.0';
import { CouponItemsDialogData } from '@interfaces/coupon/1.0';
import { MessageType } from '@constants/message/1.0';
import { Modifier } from '@interfaces/modifier/1.0';
import { MenuCategory } from '@interfaces/menu/1.0';
import { CouponService } from '@services/coupon/1.0/coupon.service';

import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';
import { MessageComponent } from '../../message/1.0/message.component';
import { TypeSelectorComponent } from '../../type-selector/1.0/type-selector.component';
import { ItemListComponent } from '../../item-list/1.0/item-list.component';
import { ButtonComponent } from '../../button/1.0/button.component';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

@Component({
  selector: 'txp-coupon-items-dialog',
  templateUrl: './coupon-items-dialog.component.html',
  styleUrls: ['./coupon-items-dialog.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MessageComponent,
    TypeSelectorComponent,
    NgFor,
    ItemListComponent,
    ButtonComponent,
    MatFormField,
    FormsModule,
    ReactiveFormsModule,
    MatLabel,
    MatInput,
    MatHint,
    MatError,
    TranslateModule,
  ],
})
export class CouponItemsDialogComponent implements OnInit {
  buttonStyle = ButtonStyle;
  messageType = MessageType;

  form!: UntypedFormGroup;
  showError = false;
  isLoading = false;
  selectedItems: CatalogItem[] = []; // list of checked/unchecked products
  addedItemsList: CatalogItem[] = []; // list of added product to a Category
  productsList: Product[] = []; // list of available products passed to modal
  combosList: Composite[] = [];
  modifiersList: Modifier[] = [];
  categoriesList: MenuCategory[] = [];
  isProductTypeSelected = true;
  dialogTitle = '';
  inputControl!: UntypedFormControl;
  invalidIdsError = false;
  invalidIdsList: string[] = [];
  addedItemIds: string[] = [];
  lists: (Product[] | Composite[] | Modifier[] | MenuCategory[])[] = [];
  itemTypes: {
    data: 'products' | 'combos' | 'modifiers' | 'categories';
    label: string;
  }[] = [];
  itemTypeSelected!: 'products' | 'combos' | 'modifiers' | 'categories';

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<CouponItemsDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: CouponItemsDialogData | undefined,
    private translate: TranslateService,
    private couponService: CouponService,
  ) {}

  ngOnInit() {
    this.selectedItems = [];
    this.productsList = [];
    this.addedItemsList = [];
    this.form = this.formBuilder.group({
      itemIds: new UntypedFormControl('', [this.commaSeparatedValidator()]),
    });

    this.initializeItemTypes();

    /**
     * receive data passed to dialog box addition to component
     */
    if (this.data) {
      const dialogData = this.data;
      // setup lists
      this.productsList = dialogData.productsList?.slice() || [];
      this.combosList = dialogData.combosList?.slice() || [];
      this.modifiersList = dialogData.modifiersList?.slice() || [];
      this.categoriesList = dialogData.categoriesList?.slice() || [];

      if (dialogData.source === 'menuCrossSell') {
        this.lists = [this.productsList, this.combosList, this.categoriesList];
      } else {
        this.lists = [this.productsList, this.combosList, this.modifiersList];
      }

      if (dialogData.selectedItems) {
        // translate simple items into full data
        this.addedItemsList = [];
        dialogData.selectedItems.forEach((item) => {
          let addItem;
          const typedItem = item as any;
          const type = typedItem?.type || typedItem?.entityType;
          const itemId = typedItem?.id || typedItem?.catalogEntryId;
          if (type === 'PRODUCT') {
            addItem = dialogData.productLookup[itemId];
            if (addItem && dialogData.dialogType === 'search') {
              const index = this.productsList.indexOf(addItem);
              this.productsList.splice(index, 1);
            }
          } else if (type === 'COMPOSITE') {
            addItem = dialogData.comboLookup[itemId];
            if (addItem && dialogData.dialogType === 'search') {
              const index = this.combosList.indexOf(addItem);
              this.combosList.splice(index, 1);
            }
          } else if (type === 'MODIFIER') {
            addItem = dialogData.modifierLookup[itemId];
            if (addItem && dialogData.dialogType === 'search') {
              const index = this.modifiersList.indexOf(addItem);
              this.modifiersList.splice(index, 1);
            }
          } else if (type === 'CATEGORY') {
            addItem = dialogData.categoryLookup?.[itemId];
            if (addItem && dialogData.dialogType === 'search') {
              const index = this.categoriesList.indexOf(addItem);
              this.categoriesList.splice(index, 1);
            }
          }
          if (addItem) {
            this.addedItemsList.push(addItem);
          }
        });
        // reset array references to trigger  data source update
        this.form.get('itemIds')!.setValue(this.addedItemsList.map((item) => item.catalogEntryId));
        this.productsList = this.productsList?.slice();
        this.combosList = this.combosList?.slice();
        this.modifiersList = this.modifiersList?.slice();
        this.categoriesList = this.categoriesList?.slice();
      }
      this.productsList?.sort(this.compareItems);
      this.combosList?.sort(this.compareItems);
      this.refreshLists();
    }
  }

  private initializeItemTypes(): void {
    if (this.data?.source === 'menuCrossSell') {
      this.itemTypes = [
        {
          data: 'products',
          label: this.translate.instant('coupons.edit.customer.items.products'),
        },
        {
          data: 'combos',
          label: this.translate.instant('coupons.edit.customer.items.combos'),
        },
        {
          data: 'categories',
          label: this.translate.instant('coupons.edit.customer.items.categories'),
        },
      ];
    } else {
      this.itemTypes = [
        {
          data: 'products',
          label: this.translate.instant('coupons.edit.customer.items.products'),
        },
        {
          data: 'combos',
          label: this.translate.instant('coupons.edit.customer.items.combos'),
        },
        {
          data: 'modifiers',
          label: this.translate.instant('coupons.edit.customer.items.modifiers'),
        },
      ];
    }

    this.itemTypeSelected = this.itemTypes[0].data;
  }

  private refreshLists(): void {
    if (this.data?.source === 'menuCrossSell') {
      this.lists = [this.productsList, this.combosList, this.categoriesList];
      return;
    }

    this.lists = [this.productsList, this.combosList, this.modifiersList];
  }

  onAddIds() {
    this.invalidIdsError = false;
    this.invalidIdsList = [];
    let itemIds = [];

    const formValue = this.form?.value?.itemIds;
    if (Array.isArray(formValue)) {
      itemIds = formValue.map((id) => id.trim()).filter((id) => id !== '');
    } else if (typeof formValue === 'string') {
      itemIds = formValue
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id !== '');
    }

    this.addedItemIds = itemIds;

    if (itemIds.length === 0 || itemIds[0] === '') {
      this.addedItemsList = [];
      return;
    }

    // Step 1: Check which items are catalog IDs (exist in productsList/combosList)
    const allCatalogItems = [...this.productsList, ...this.combosList, ...this.modifiersList];
    const catalogIdSet = new Set(allCatalogItems.map((item) => item.catalogEntryId));

    const foundByCatalogId: string[] = [];
    const potentialPlus: string[] = [];

    itemIds.forEach((itemId) => {
      if (catalogIdSet.has(itemId)) {
        foundByCatalogId.push(itemId);
      } else {
        potentialPlus.push(itemId);
      }
    });

    // Step 2: Add items found by catalog ID to the list
    this.addedItemsList = [];
    foundByCatalogId.forEach((catalogId) => {
      const item = allCatalogItems.find((item) => item.catalogEntryId === catalogId);
      if (item) {
        this.addedItemsList.push(item);
      }
    });

    // Step 3: If there are potential PLUs, search for them via API
    if (potentialPlus.length > 0 && this.data?.brandId) {
      this.isLoading = true;
      this.couponService.getCatalogEntriesByPlu(this.data.brandId, potentialPlus).subscribe({
        next: (results) => {
          // Build a map of catalogEntryId -> PLU(s) (array)
          const catalogIdToPlus: { [catalogId: string]: string[] } = {};
          if (results && results.length > 0) {
            results.forEach((result) => {
              if (!catalogIdToPlus[result.catalogId]) {
                catalogIdToPlus[result.catalogId] = [];
              }
              if (!catalogIdToPlus[result.catalogId].includes(result.plu)) {
                catalogIdToPlus[result.catalogId].push(result.plu);
              }
            });

            // Check if all PLUs were found in the response
            const foundPluValues = new Set(results.map((r) => r.plu));
            const notFoundPlus = potentialPlus.filter((plu) => !foundPluValues.has(plu));

            if (notFoundPlus.length > 0) {
              // Some PLUs were not found in the API response - mark them as invalid
              this.invalidIdsList = notFoundPlus;
              this.invalidIdsError = true;
            }

            // Get unique catalog IDs from PLU results and add them to the list
            const uniqueCatalogIds = new Set<string>();
            results.forEach((result) => {
              uniqueCatalogIds.add(result.catalogId);
            });

            // Add the catalog IDs from PLU API to addedItemsList
            uniqueCatalogIds.forEach((catalogId) => {
              if (this.addedItemsList.find((i) => i.catalogEntryId === catalogId)) {
                return;
              }
              const item = allCatalogItems.find((item) => item.catalogEntryId === catalogId);
              const pluString = catalogIdToPlus[catalogId]?.join(', ');
              if (item) {
                this.addedItemsList.push({ ...(item as any), plu: pluString });
              } else {
                const basicItem = {
                  catalogEntryId: catalogId,
                  plu: pluString,
                } as any;
                this.addedItemsList.push(basicItem);
              }
            });
          } else {
            // No PLU matches found in API response
            this.invalidIdsList = potentialPlus;
            this.invalidIdsError = true;
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.invalidIdsList = potentialPlus;
          this.invalidIdsError = true;
          this.isLoading = false;
        },
      });
    } else if (potentialPlus.length > 0) {
      // If there are potential PLUs but no brandId, show error
      this.invalidIdsList = potentialPlus;
      this.invalidIdsError = true;
    }
  }

  commaSeparatedValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      const pattern = /^([a-zA-Z0-9-]+)(,\s*[a-zA-Z0-9-]+)*$/;
      if (value && !pattern.test(value)) {
        return { commaSeparated: true };
      }
      return null;
    };
  }

  /**
   * close the dialog and send data to the parent component
   */
  onDone() {
    this.form.markAllAsTouched();
    if (this.data?.dialogType === 'add') {
      this.onAddIds();
      if (this.isLoading) {
        // Wait for async PLU validation to finish, then close if valid
        const checkAndClose = () => {
          if (!this.isLoading) {
            if (!this.invalidIdsError) {
              this.invalidIdsError = false;
              const data = this.addedItemsList.map((item) => ({
                name: item.name || item.catalogEntryId,
                id: item.catalogEntryId,
                type: item.entityType,
              }));
              this.selectedItems = [];
              this.productsList = [];
              this.dialogRef.close(data);
            }
          } else {
            setTimeout(checkAndClose, 50);
          }
        };
        checkAndClose();
        return;
      }
      if (this.invalidIdsError) return;
    }
    this.invalidIdsError = false;
    const data = (this.addedItemsList as any[]).map((item) => ({
      name: item.name || item.catalogEntryId,
      id: item.catalogEntryId,
      type: item.entityType,
      plu: item.plu,
    }));
    this.selectedItems = [];
    this.productsList = [];
    this.dialogRef.close(data);
  }

  /**
   * close the dialog
   */
  onCancel() {
    this.showError = false;
    this.dialogRef.close(this.data?.selectedItems);
  }

  /**
   * add products to the menu
   */
  onAdd() {
    this.selectedItems.forEach((e: Product | Composite) => {
      this.addedItemsList.push(e);
      // remove item from appropriate list
      if (e.entityType === 'PRODUCT') {
        this.productsList.splice(this.productsList.indexOf(e), 1);
      } else if (e.entityType === 'COMPOSITE') {
        // COMBO
        this.combosList.splice(this.combosList.indexOf(e as Composite), 1);
      } else if (e.entityType === 'MODIFIER') {
        // modifier
        this.modifiersList.splice(this.modifiersList.indexOf(e as Modifier), 1);
      } else if (e.entityType === 'CATEGORY') {
        // category
        this.categoriesList.splice(this.categoriesList.indexOf(e as MenuCategory), 1);
      }
    });
    // create new array references so data source will be updated
    this.productsList = this.productsList?.slice().sort(this.compareItems);
    this.combosList = this.combosList?.slice().sort(this.compareItems);
    this.modifiersList = this.modifiersList?.slice().sort(this.compareItems);
    this.categoriesList = this.categoriesList?.slice().sort(this.compareItems);

    this.form.get('itemIds')!.setValue(this.addedItemsList.map((item) => item.catalogEntryId));

    this.selectedItems = [];
  }

  /**
   * delete items from the menu
   */
  onDelete(item: Product | Composite) {
    this.addedItemsList.splice(this.addedItemsList.indexOf(item), 1);
    if (item.entityType === 'PRODUCT') {
      this.productsList.push(item);
      this.productsList = this.productsList.slice().sort(this.compareItems);
    } else if (item.entityType === 'COMPOSITE') {
      this.combosList.push(item as Composite);
      this.combosList = this.combosList.slice().sort(this.compareItems);
    } else if (item.entityType === 'MODIFIER') {
      this.modifiersList.push(item as Modifier);
      this.modifiersList = this.modifiersList.slice().sort(this.compareItems);
    }
  }

  onSelectItem(itemType: 'products' | 'combos' | 'modifiers' | 'categories') {
    if (this.itemTypeSelected !== itemType) {
      this.selectedItems = [];
      this.itemTypeSelected = itemType;
    }
  }

  /**
   * selected products will add to the selected items array
   */
  getSelectedCatalog(eventData: CatalogItem[]) {
    this.selectedItems = eventData;
  }

  compareItems(a: { name?: string }, b: { name?: string }) {
    return (a.name || '') > (b.name || '') ? 1 : -1;
  }
}
